import { forwardRef, useId } from "react";
import type { ReactNode, SelectHTMLAttributes } from "react";
import clsx from "clsx";
import type { FieldLabelVariant } from "./Field.js";
import styles from "./Field.module.scss";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Mono uppercase label. Pass `hideLabel` when a row already names the control. */
  label: string;
  /** Keep the label for screen readers only — inline selects inside a row have a visible name already. */
  hideLabel?: boolean;
  error?: string;
  hint?: ReactNode;
  /** `caption` (default) — mono, tiny, tracked; the shipped majority. `heading` — Inter, a step larger,
   * full ink, matching a section label: for a surface that is ONE group of titled fields. */
  labelVariant?: FieldLabelVariant;
  /** Guidance shown BETWEEN the label and the control — what to type. Use `hint` for a note that belongs
   * UNDER the control instead. */
  subLabel?: ReactNode;
  children: ReactNode;
}

/**
 * The `<select>` sibling of {@link Field}, sharing `.control`. Keeps the **native** arrow and picker
 * (the platform control is the accessible one, and on iOS it's the system wheel) — `.select` only
 * reserves room for it. It exists because `Field` is `<input>`-only, which is why **18 `.select`
 * rules** had been hand-rolled.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, labelVariant, subLabel, hideLabel, error, hint, id, className, children, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const subId = `${fieldId}-sub`;
  const describedBy = [error ? errorId : null, hint ? hintId : null, subLabel ? subId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={clsx(styles.field, error && styles.hasError, className)}>
      <label htmlFor={fieldId} className={hideLabel ? styles.srOnly : styles.label} data-variant={labelVariant}>
        {label}
      </label>
      {subLabel && (
        <p id={subId} className={styles.subLabel}>
          {subLabel}
        </p>
      )}
      <select
        ref={ref}
        id={fieldId}
        className={clsx(styles.control, styles.select)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      >
        {children}
      </select>
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
